import Skeleton from "@mui/material/Skeleton";
import styled, { css } from "styled-components";

export type ShopActionFeedbackTone = "success" | "error";

const toneStyles = {
  success: css`
    border-color: rgba(149, 208, 83, 0.28);
    background: linear-gradient(135deg, rgba(86, 124, 48, 0.22), rgba(32, 45, 24, 0.92));
    box-shadow: 0 18px 44px rgba(16, 26, 13, 0.22);
  `,
  error: css`
    border-color: rgba(227, 118, 118, 0.3);
    background: linear-gradient(135deg, rgba(120, 45, 45, 0.26), rgba(47, 19, 19, 0.94));
    box-shadow: 0 18px 44px rgba(32, 10, 10, 0.24);
  `,
};

const badgeStyles = {
  success: css`
    background: rgba(178, 236, 118, 0.16);
    color: #dcf5b8;
  `,
  error: css`
    background: rgba(255, 164, 164, 0.14);
    color: #ffd1d1;
  `,
};

const messageStyles = {
  success: css`
    color: #edf7df;
  `,
  error: css`
    color: #ffe1e1;
  `,
};

export const FeedbackRoot = styled.div<{ $tone: ShopActionFeedbackTone; $stackIndex: number }>`
  position: fixed;
  right: 1.5rem;
  bottom: calc(env(safe-area-inset-bottom, 0px) + 1.5rem + ${({ $stackIndex }) => $stackIndex * 6.25}rem);
  z-index: 1300;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: 1.15rem;
  width: min(27rem, calc(100vw - 3rem));
  min-height: 4.5rem;
  isolation: isolate;
  pointer-events: none;
  ${({ $tone }) => toneStyles[$tone]};

  @media (max-width: 720px) {
    right: 1rem;
    left: 1rem;
    width: auto;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem + ${({ $stackIndex }) => $stackIndex * 5.75}rem);
    border-radius: 1rem;
  }
`;

export const FeedbackSkeleton = styled(Skeleton)<{ $tone: ShopActionFeedbackTone }>`
  transform: none;
  border-radius: inherit;
  width: 100%;
  height: 100%;
  min-height: 4.5rem;
  opacity: 0.92;
  background: ${({ $tone }) =>
    $tone === "success"
      ? "linear-gradient(90deg, rgba(140, 201, 86, 0.1), rgba(221, 241, 193, 0.22), rgba(140, 201, 86, 0.1))"
      : "linear-gradient(90deg, rgba(232, 128, 128, 0.12), rgba(255, 222, 222, 0.2), rgba(232, 128, 128, 0.12))"};
`;

export const FeedbackContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.95rem 1.1rem 1rem;
`;

export const FeedbackLabel = styled.span<{ $tone: ShopActionFeedbackTone }>`
  width: fit-content;
  border-radius: 999px;
  padding: 0.24rem 0.58rem;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  ${({ $tone }) => badgeStyles[$tone]};
`;

export const FeedbackMessage = styled.p<{ $tone: ShopActionFeedbackTone }>`
  margin: 0;
  font-size: 0.96rem;
  line-height: 1.5;
  ${({ $tone }) => messageStyles[$tone]};
`;
